/** @format */

import React, {useState, useEffect, useRef} from 'react';
import {Input, IconButton} from '@sttark/design-system';
import {getMaxRollDiameterForWidth} from 'frontier/utilities/roll_min_max_validator';

/**
 * RollConstrainedInput - A controlled input component for roll diameter values
 * that handles validation constraints in a predictable way.
 *
 * Features:
 * - Allows free typing without interference
 * - Shows validation hints in real-time
 * - Applies constraints only on commit (blur/enter)
 * - Clear feedback about what constraints will be applied
 */
export default function RollConstrainedInput({
    value,
    onChange,
    onCommit,
    label,
    suffix,
    disabled,
    error,
    width,
    productTypeId,
    minValue = 4,
    maxValue,
    inputType = 'diameter', // 'diameter' or 'quantity'
    className,
    ...props
}) {
    // Validate and sanitize props to prevent NaN issues
    const safeMinValue = isNaN(minValue) ? (inputType === 'diameter' ? 4 : 100) : minValue;
    // For diameter inputs, don't use a default maxValue - let it be calculated based on width
    const safeMaxValue = maxValue !== undefined && !isNaN(maxValue) ? maxValue : undefined;
    const [displayValue, setDisplayValue] = useState(value || '');
    const [isEditing, setIsEditing] = useState(false);
    const [validationHint, setValidationHint] = useState('');
    const inputRef = useRef(null);

    // Update display value when prop value changes (external updates)
    useEffect(() => {
        if (!isEditing) {
            setDisplayValue(value || '');
        }
    }, [value, isEditing]);

    // Auto-adjust value when width changes and current value exceeds new limits
    useEffect(() => {
        if (inputType === 'diameter' && value && width) {
            const newMaxAllowed = getMaxRollDiameterForWidth(width, productTypeId);

            // If current value exceeds the new width-based limit, trigger onCommit to adjust it
            if (parseFloat(value) > newMaxAllowed) {
                console.log(
                    `Auto-adjusting diameter from ${value}" to ${newMaxAllowed}" due to width change`
                );
                if (onCommit) {
                    onCommit(newMaxAllowed);
                }
            }
        }
    }, [width, productTypeId, value, inputType, onCommit]);

    // Calculate constraints based on input type
    const calculatedMaxDiameter =
        inputType === 'diameter' ? getMaxRollDiameterForWidth(width, productTypeId) : 10000;
    const maxAllowed = safeMaxValue || calculatedMaxDiameter;

    // Debug logging for narrow labels
    if (inputType === 'diameter' && width && width < 0.875) {
        console.log('Narrow label detected:', {
            width,
            calculatedMaxDiameter,
            safeMaxValue,
            maxAllowed
        });
    }

    // Real-time validation hint calculation
    useEffect(() => {
        if (!isEditing || displayValue === '') {
            setValidationHint('');
            return;
        }

        const numValue = parseFloat(displayValue);
        if (isNaN(numValue)) {
            setValidationHint('Must be a valid number');
            return;
        }

        if (numValue < safeMinValue) {
            const unit = inputType === 'diameter' ? '"' : '';
            setValidationHint(`Will be set to minimum: ${safeMinValue}${unit}`);
            return;
        }

        if (numValue > maxAllowed) {
            const unit = inputType === 'diameter' ? '"' : '';
            setValidationHint(`Will be set to maximum: ${maxAllowed}${unit}`);
            return;
        }

        setValidationHint('');
    }, [displayValue, isEditing, safeMinValue, maxAllowed, inputType]);

    const handleFocus = () => {
        setIsEditing(true);
    };

    const handleChange = newValue => {
        setDisplayValue(newValue);
        // Call onChange for immediate state updates if needed
        if (onChange) {
            onChange(newValue);
        }
    };

    const handleCommit = () => {
        setIsEditing(false);

        if (displayValue === '') {
            if (onCommit) onCommit('');
            return;
        }

        let numValue = parseFloat(displayValue);

        if (isNaN(numValue)) {
            // Reset to original value if invalid
            setDisplayValue(value || '');
            return;
        }

        // Apply constraints
        if (numValue < safeMinValue) {
            numValue = safeMinValue;
        }
        if (numValue > maxAllowed) {
            numValue = maxAllowed;
        }

        setDisplayValue(numValue.toString());

        if (onCommit) {
            onCommit(numValue);
        }
    };

    const handleBlur = () => {
        handleCommit();
    };

    const handleApplyClick = () => {
        inputRef.current?.blur(); // This will trigger handleBlur which calls handleCommit
    };

    const handleKeyUp = e => {
        if (e.key === 'Enter') {
            e.preventDefault();
            inputRef.current?.blur(); // This will trigger handleBlur which calls handleCommit
        }
        if (e.key === 'Escape') {
            e.preventDefault();
            setDisplayValue(value || '');
            setIsEditing(false);
            inputRef.current?.blur();
        }
    };

    // Determine what error/hint to show
    let displayError = error;
    if (isEditing && validationHint && !error) {
        displayError = validationHint;
    }

    return (
        <div className={className}>
            <div className="position-relative">
                <Input
                    ref={inputRef}
                    type="number"
                    label={label}
                    suffix={suffix}
                    value={displayValue}
                    onChange={handleChange}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    onKeyUp={handleKeyUp}
                    disabled={disabled}
                    error={displayError}
                    {...props}
                />
                {isEditing && !disabled && (
                    <div
                        className="position-absolute"
                        style={{
                            right: '8px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            zIndex: 10
                        }}>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={handleApplyClick}
                            title="Save"
                            style={{fontSize: '12px', padding: '2px 6px'}}>
                            Save
                        </button>
                    </div>
                )}
            </div>
            {!error && (
                <div className="mt-1 text-muted small">
                    Range: {safeMinValue}
                    {inputType === 'diameter' ? '"' : ''} -{' '}
                    {isNaN(maxAllowed)
                        ? inputType === 'diameter'
                            ? calculatedMaxDiameter
                            : '10,000'
                        : maxAllowed}
                    {inputType === 'diameter' ? '"' : ''}
                </div>
            )}
        </div>
    );
}
